import type { ChangeEvent } from 'react'
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useStyles } from './AzurePricingSection.styles'
import clsx from 'clsx'
import { TextInput } from '@labrav/react-components'
import { get, isEmpty } from 'lodash'
import { getDiscountedPriceValue, isNonEmptyNumber } from '@labrav/common'
import {
  AdditionalMeter,
  AzureOfferPlan,
  AzurePricingType,
  IncludedQuantity,
} from '../../../modules/privateOffer/types'
import { DisplayValueData } from './DisplayValueData'

export interface AzureMeteringDimensionsProps {
  planRow: AzureOfferPlan
  index: number
  updatePlanId?: (
    index: number,
    plan: AzureOfferPlan,
    keyForErrorCheck?: string,
    subIndex?: number,
    meter?: string
  ) => Promise<void>
  planRowError?: Record<string, unknown>
  azurePricingType: AzurePricingType
  isReadOnly?: boolean
  isViewPage?: boolean
}

export const AzureMeteringDimensions = ({
  planRow,
  updatePlanId,
  planRowError,
  index,
  azurePricingType,
  isReadOnly = false,
  isViewPage = false,
}: AzureMeteringDimensionsProps) => {
  const { classes } = useStyles()

  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const planPrices = planRow?.pricing?.recurrentPrice?.prices || []

  const shouldReadOnlyValue = isDiscountPricing || isReadOnly

  const dynamicColumnForMetering = planPrices.map(
    planPrice =>
      `Base quantity (${planPrice.billingTerm.value} ${planPrice.billingTerm.type})`
  )

  const meteringList =
    !isDiscountPricing || isViewPage
      ? { ...planRow.pricing.customMeters?.meters }
      : { ...planRow?.originalPricing?.customMeters?.meters }

  const meteringColumn = [
    'Name',
    'Unit of measure',
    `${isDiscountPricing ? 'Discounted price' : 'Price'} per unit (USD)`,
    ...dynamicColumnForMetering,
  ]

  const updatePlan = (
    index: number,
    meteringKey: string,
    updatedMeter: AdditionalMeter,
    key: string,
    meterQuantityIndex?: number
  ) => {
    if (planRow?.pricing?.customMeters) {
      const customMetersData = {
        ...planRow?.pricing?.customMeters,
        meters: {
          ...planRow?.pricing?.customMeters.meters,
          [meteringKey]: updatedMeter,
        },
      }
      const updatePlanRow = {
        ...planRow,
        pricing: {
          ...planRow.pricing,
          customMeters: {
            ...customMetersData,
          },
        },
      }
      updatePlanId &&
        updatePlanId(
          index,
          { ...updatePlanRow },
          key,
          meterQuantityIndex,
          meteringKey
        )
    }
  }

  const tableDataCellClass = clsx(
    classes.rowText,
    classes.tableCell,
    isReadOnly ? classes.readOnlyTableCell : ''
  )

  return (
    <div>
      <div
        className={clsx(
          classes.subHeadingContainer,
          classes.companyDetailSubHead,
          classes.metering
        )}
      >
        <Typography
          className={clsx(
            classes.subHeading,
            isReadOnly ? classes.subHeadingReadOnly : ''
          )}
          data-testid="metering-heading"
        >
          Metering dimensions
        </Typography>
      </div>

      <div>
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead className={classes.tableHader}>
              <TableRow>
                {meteringColumn.map(col => (
                  <TableCell
                    className={clsx(
                      classes.headerTitle,
                      classes.tableCell,
                      isReadOnly ? classes.readOnlyTableCell : ''
                    )}
                    data-testid={'metering-column'}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {meteringList &&
                Object.keys(meteringList).map(meteringKey => {
                  const meter = { ...meteringList[meteringKey] }
                  const meterErrorObject = planRowError
                    ? get(planRowError.meters, meteringKey)
                    : {}
                  const updateMeter =
                    (
                      key: 'pricePerPaymentInUsd' | 'quantity' | 'isInfinite',
                      meterQuantityIndex?: number
                    ) =>
                    (
                      event: ChangeEvent<HTMLInputElement>,
                      checked?: boolean
                    ) => {
                      const updatedIncludedQuantities = [
                        ...meter.includedQuantities,
                      ]
                      if (
                        key !== 'pricePerPaymentInUsd' &&
                        meterQuantityIndex !== undefined
                      )
                        updatedIncludedQuantities[meterQuantityIndex] = {
                          ...updatedIncludedQuantities[meterQuantityIndex],
                          [key]:
                            key === 'isInfinite'
                              ? checked
                              : parseInt(event.target.value),
                        }
                      const updatedMeter = {
                        ...meter,
                        ...(key === 'pricePerPaymentInUsd'
                          ? {
                              pricePerPaymentInUsd:
                                event.target.value !== ''
                                  ? parseFloat(
                                      Number(event.target.value).toFixed(3)
                                    )
                                  : event.target.value,
                            }
                          : {}),
                        includedQuantities: [...updatedIncludedQuantities],
                      }
                      updatePlan(
                        index,
                        meteringKey,
                        updatedMeter as AdditionalMeter,
                        key,
                        meterQuantityIndex
                      )
                    }
                  //TODO: https://ibexlabs.atlassian.net/browse/LT-5652
                  return (
                    <TableRow>
                      <TableCell className={tableDataCellClass} width={'150px'}>
                        <div>{meter.meteringDimensionName}</div>
                      </TableCell>
                      <TableCell className={tableDataCellClass} width={'120px'}>
                        <div>{meter.unitOfMeasure}</div>
                      </TableCell>
                      <TableCell className={tableDataCellClass} width={'220px'}>
                        {!shouldReadOnlyValue ? (
                          <TextInput
                            type="number"
                            title="Price per unit ($)"
                            placeHolder="Price per unit ($)"
                            errorText={
                              meterErrorObject?.pricePerPaymentInUsd as string
                            }
                            isError={
                              !isEmpty(
                                meterErrorObject?.pricePerPaymentInUsd || ''
                              )
                            }
                            isReadOnly={false}
                            isRequired={true}
                            value={
                              isNonEmptyNumber(meter.pricePerPaymentInUsd)
                                ? meter.pricePerPaymentInUsd
                                : ''
                            }
                            updatedValue={
                              isNonEmptyNumber(meter.pricePerPaymentInUsd)
                                ? meter.pricePerPaymentInUsd
                                : ''
                            }
                            onChange={updateMeter('pricePerPaymentInUsd')}
                            keyName="pricePerPaymentInUsd"
                            minNumber={0.001}
                            variant="regularV2"
                            keyToPrevent={['+', '-', 'e']}
                          />
                        ) : (
                          <DisplayValueData
                            isPrice={true}
                            id="pricePerPaymentInUsd"
                            value={
                              isViewPage
                                ? meter.pricePerPaymentInUsd
                                : getDiscountedPriceValue({
                                    price: meter.pricePerPaymentInUsd,
                                    ...(isDiscountPricing
                                      ? {
                                          discount:
                                            planRow?.discountPercentage || 0,
                                        }
                                      : {}),
                                  })
                            }
                          />
                        )}
                      </TableCell>
                      {meter.includedQuantities.map(
                        (
                          qunatityItem: IncludedQuantity,
                          quantityIndex: number
                        ) => {
                          const label = `Base quantity (${qunatityItem.billingTerm.value} ${qunatityItem.billingTerm.type})`

                          const errQunatity = get(
                            meterErrorObject,
                            'includedQuantities'
                          )
                            ? get(meterErrorObject, 'includedQuantities')[
                                quantityIndex
                              ]
                            : {}
                          return (
                            <TableCell
                              className={tableDataCellClass}
                              width={'220px'}
                            >
                              {!shouldReadOnlyValue ? (
                                <div className={classes.tableInput}>
                                  <TextInput
                                    type="number"
                                    title={label}
                                    placeHolder={label}
                                    errorText={
                                      (errQunatity.quantity || '') as string
                                    }
                                    isError={
                                      !qunatityItem.isInfinite &&
                                      !isEmpty(errQunatity.quantity || '')
                                    }
                                    isReadOnly={qunatityItem.isInfinite}
                                    isRequired={true}
                                    value={
                                      isNonEmptyNumber(qunatityItem.quantity)
                                        ? qunatityItem.quantity
                                        : ''
                                    }
                                    updatedValue={
                                      isNonEmptyNumber(qunatityItem.quantity)
                                        ? qunatityItem.quantity
                                        : ''
                                    }
                                    onChange={updateMeter(
                                      'quantity',
                                      quantityIndex
                                    )}
                                    keyName="baseQuantity"
                                    minNumber={1}
                                    variant="regularV2"
                                  />
                                  <div>
                                    <Checkbox
                                      defaultChecked={
                                        qunatityItem.isInfinite || false
                                      }
                                      className={classes.checkboxLeft}
                                      color="default"
                                      checked={Boolean(
                                        qunatityItem.isInfinite || false
                                      )}
                                      name="isInfinite"
                                      id="isInfinite"
                                      onChange={updateMeter(
                                        'isInfinite',
                                        quantityIndex
                                      )}
                                      inputProps={
                                        {
                                          'data-testid': 'checkbox-item',
                                        } as never
                                      }
                                    />
                                    Unlimited
                                  </div>
                                </div>
                              ) : (
                                <DisplayValueData
                                  id="baseQuantity"
                                  value={qunatityItem.quantity}
                                  isUnlimited={qunatityItem?.isInfinite}
                                />
                              )}
                            </TableCell>
                          )
                        }
                      )}
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}
